import React, { useEffect, useMemo, useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { hasCapability, CAPABILITY } from "@/lib/capabilityPolicy";
import { useLookupValues } from "@/lib/platformConfig";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldAlert,
  UserPlus,
  LogOut,
  Award,
  GraduationCap,
  Building,
  Wrench,
  Shirt,
  AlertOctagon,
} from "lucide-react";
import { formatMoney, formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { DatePicker } from "@/components/ui/calendar";

export default function Campus() {
  const { t } = useI18n();
  return (
    <div className="p-6 lg:p-8" data-testid="campus-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("management.campusTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("management.campusSubtitle")}
        </p>
      </div>
      <Tabs defaultValue="safety">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="safety" data-testid="tab-safety">
            <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Safety
          </TabsTrigger>
          <TabsTrigger value="discipline" data-testid="tab-discipline">
            <Award className="h-3.5 w-3.5 mr-1.5" /> Discipline
          </TabsTrigger>
          <TabsTrigger value="alumni" data-testid="tab-alumni">
            <GraduationCap className="h-3.5 w-3.5 mr-1.5" /> Alumni
          </TabsTrigger>
          <TabsTrigger value="placement" data-testid="tab-placement">
            <Building className="h-3.5 w-3.5 mr-1.5" /> Placement
          </TabsTrigger>
          <TabsTrigger value="hostel-svc" data-testid="tab-hostel-svc">
            <Wrench className="h-3.5 w-3.5 mr-1.5" /> Hostel svc
          </TabsTrigger>
        </TabsList>
        <TabsContent value="safety">
          <SafetyTab />
        </TabsContent>
        <TabsContent value="discipline">
          <DisciplineTab />
        </TabsContent>
        <TabsContent value="alumni">
          <AlumniTab />
        </TabsContent>
        <TabsContent value="placement">
          <PlacementTab />
        </TabsContent>
        <TabsContent value="hostel-svc">
          <HostelSvcTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============== SAFETY
function SafetyTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManageAlerts = hasCapability(user, CAPABILITY.SAFETY_MANAGE);
  const { data: visitors } = useQuery({
    queryKey: ["visitors"],
    queryFn: async () => unwrap(await api.get("/visitors")),
  });
  const { data: alerts } = useQuery({
    queryKey: ["panic"],
    enabled: canManageAlerts,
    queryFn: async () => unwrap(await api.get("/panic-alerts")),
  });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    purpose: "",
    meeting_person: "",
  });

  const addMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/visitors", form)),
    onSuccess: () => {
      toast.success("Visitor checked in");
      setShowAdd(false);
      setForm({ name: "", phone: "", purpose: "", meeting_person: "" });
      qc.invalidateQueries({ queryKey: ["visitors"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const checkoutMut = useMutation({
    mutationFn: async (vid) =>
      unwrap(await api.patch(`/visitors/${vid}/checkout`)),
    onSuccess: () => {
      toast.success("Checked out");
      qc.invalidateQueries({ queryKey: ["visitors"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const resolveMut = useMutation({
    mutationFn: async (pid) =>
      unwrap(
        await api.post(`/panic-alerts/${pid}/resolve`, {
          note: "Handled by admin",
        }),
      ),
    onSuccess: () => {
      toast.success("Alert resolved");
      qc.invalidateQueries({ queryKey: ["panic"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const open = (visitors || []).filter((v) => v.status === "in");
  const openAlerts = (alerts || []).filter((a) => a.status === "open");

  return (
    <div className="mt-6 space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        <StatCard
          label="Visitors on campus"
          value={open.length}
          icon={<UserPlus className="h-4 w-4" />}
        />
        <StatCard
          label="Open alerts"
          value={openAlerts.length}
          icon={<AlertOctagon className="h-4 w-4 text-[var(--error)]" />}
          accent="error"
        />
        <StatCard
          label="Visits today (total)"
          value={(visitors || []).length}
          icon={<LogOut className="h-4 w-4" />}
        />
      </div>

      {canManageAlerts && openAlerts.length > 0 && (
        <Card className="border-[var(--error)] border-2">
          <CardHeader>
            <CardTitle className="text-sm font-display text-[var(--error)] flex items-center gap-2">
              <AlertOctagon className="h-4 w-4" /> Active panic alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {openAlerts.map((a) => (
              <div
                key={a.id}
                className="border-l-4 border-[var(--error)] bg-[var(--error-bg)] p-3 rounded flex items-center justify-between"
                data-testid={`alert-${a.id}`}
              >
                <div>
                  <div className="font-semibold text-sm">
                    {a.student_name}{" "}
                    <span className="text-xs text-[var(--text-muted)]">
                      · {a.location}
                    </span>
                  </div>
                  <div className="text-sm mt-0.5">{a.message}</div>
                </div>
                <Button
                  size="sm"
                  onClick={() => resolveMut.mutate(a.id)}
                  data-testid={`resolve-${a.id}`}
                >
                  Resolve
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display">Visitor log</CardTitle>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="new-visitor-btn"
              >
                <UserPlus className="h-4 w-4 mr-1.5" /> Check in visitor
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New visitor</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="visitor-name"
                />
                <Input
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="visitor-phone"
                />
                <Input
                  placeholder="Purpose (e.g. parent meeting)"
                  value={form.purpose}
                  onChange={(e) =>
                    setForm({ ...form, purpose: e.target.value })
                  }
                  data-testid="visitor-purpose"
                />
                <Input
                  placeholder="Meeting person (optional)"
                  value={form.meeting_person}
                  onChange={(e) =>
                    setForm({ ...form, meeting_person: e.target.value })
                  }
                  data-testid="visitor-meeting-person"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={() => addMut.mutate()}
                  disabled={
                    !form.name ||
                    !form.phone ||
                    !form.purpose ||
                    addMut.isPending
                  }
                  data-testid="visitor-save"
                >
                  Check in
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-3">Pass</th>
                <th className="text-left px-3 py-3">Name</th>
                <th className="text-left px-3 py-3">Purpose</th>
                <th className="text-left px-3 py-3">Meeting</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {(visitors || []).map((v) => (
                <tr key={v.id} data-testid={`visitor-${v.id}`}>
                  <td className="px-3 py-3 font-mono-num text-xs">
                    {v.pass_no}
                  </td>
                  <td className="px-3 py-3 font-medium">
                    {v.name}
                    <div className="text-[10px] text-[var(--text-muted)] font-mono-num">
                      {v.phone}
                    </div>
                  </td>
                  <td className="px-3 py-3">{v.purpose}</td>
                  <td className="px-3 py-3 text-[var(--text-secondary)]">
                    {v.meeting_person || "—"}
                  </td>
                  <td className="px-3 py-3">
                    <Badge
                      className={
                        v.status === "in"
                          ? "bg-[var(--success-bg)] text-[var(--success)] border-0"
                          : "bg-[var(--subtle-bg)] text-[var(--text-muted)] border-0"
                      }
                    >
                      {v.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-3">
                    {v.status === "in" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => checkoutMut.mutate(v.id)}
                        className="h-8"
                        data-testid={`checkout-${v.id}`}
                      >
                        Check out
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {(visitors || []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center text-[var(--text-muted)]"
                  >
                    No visitors yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== DISCIPLINE
function DisciplineTab() {
  const qc = useQueryClient();
  const disciplineLookup = useLookupValues("discipline_kinds");
  const { data } = useQuery({
    queryKey: ["discipline"],
    queryFn: async () => unwrap(await api.get("/discipline-incidents")),
  });
  const { data: students } = useQuery({
    queryKey: ["students-lite"],
    queryFn: async () => unwrap(await api.get("/students?page_size=100")),
  });
  const [show, setShow] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    student_id: "",
    kind: "",
    category: "",
    points: 0,
    description: "",
    date: today,
  });

  const kindOptions = useMemo(() => {
    const rows = disciplineLookup.data?.values || [];
    return rows.map((row) => ({
      value: row.value,
      label: row.label,
      defaultPoints: Number.isFinite(row?.metadata?.default_points)
        ? Number(row.metadata.default_points)
        : undefined,
    }));
  }, [disciplineLookup.data?.values]);

  const defaultPointsForKind = (kind) => {
    const option = kindOptions.find((row) => row.value === kind);
    if (typeof option?.defaultPoints === "number") return option.defaultPoints;
    if (kind === "reward") return 5;
    if (kind === "warning") return -2;
    return 0;
  };

  useEffect(() => {
    if (!kindOptions.length) return;
    if (kindOptions.some((row) => row.value === form.kind)) return;
    setForm((prev) => ({
      ...prev,
      kind: kindOptions[0].value,
      points: defaultPointsForKind(kindOptions[0].value),
    }));
  }, [kindOptions, form.kind]);

  const addMut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post("/discipline-incidents", {
          ...form,
          points: Number(form.points),
        }),
      ),
    onSuccess: () => {
      toast.success("Incident recorded");
      setShow(false);
      qc.invalidateQueries({ queryKey: ["discipline"] });
      setForm({ ...form, description: "" });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const rewards = (data || []).filter((d) => d.kind === "reward").length;
  const warnings = (data || []).filter((d) => d.kind === "warning").length;

  return (
    <div className="mt-6 space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        <StatCard
          label="Total incidents"
          value={(data || []).length}
          icon={<Award className="h-4 w-4" />}
        />
        <StatCard
          label="Rewards"
          value={rewards}
          icon={<Award className="h-4 w-4 text-[var(--success)]" />}
          accent="success"
        />
        <StatCard
          label="Warnings"
          value={warnings}
          icon={<AlertOctagon className="h-4 w-4 text-[var(--error)]" />}
          accent="error"
        />
      </div>
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display">Behaviour log</CardTitle>
          <Dialog open={show} onOpenChange={setShow}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="new-incident-btn"
              >
                <Award className="h-4 w-4 mr-1.5" /> New incident
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record discipline incident</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Select
                  value={form.student_id}
                  onValueChange={(v) => setForm({ ...form, student_id: v })}
                >
                  <SelectTrigger data-testid="incident-student">
                    <SelectValue placeholder="Select student" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(students?.items || students || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.first_name} {s.last_name} · {s.admission_no}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    value={form.kind}
                    onValueChange={(v) =>
                      setForm({
                        ...form,
                        kind: v,
                        points: defaultPointsForKind(v),
                      })
                    }
                  >
                    <SelectTrigger data-testid="incident-kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {kindOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={form.points}
                    onChange={(e) =>
                      setForm({ ...form, points: e.target.value })
                    }
                    data-testid="incident-points"
                    placeholder="Points"
                  />
                </div>
                <Input
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  placeholder="Category (uniform/homework/leadership...)"
                  data-testid="incident-category"
                />
                <DatePicker
                  value={form.date}
                  onChange={(date) => setForm({ ...form, date })}
                  testId="incident-date"
                />
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Description"
                  data-testid="incident-desc"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={() => addMut.mutate()}
                  disabled={
                    !form.student_id || !form.description || addMut.isPending
                  }
                  data-testid="incident-save"
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-3">Date</th>
                <th className="text-left px-3 py-3">Student</th>
                <th className="text-left px-3 py-3">Kind</th>
                <th className="text-left px-3 py-3">Category</th>
                <th className="text-right px-3 py-3">Points</th>
                <th className="text-left px-3 py-3">Description</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((d) => (
                <tr key={d.id} data-testid={`incident-${d.id}`}>
                  <td className="px-3 py-3 font-mono-num text-xs">
                    {formatDate(d.date)}
                  </td>
                  <td className="px-3 py-3 font-medium">
                    {d.student_name}
                    <div className="text-[10px] text-[var(--text-muted)] font-mono-num">
                      {d.student_admission_no}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <Badge
                      className={
                        d.kind === "reward"
                          ? "bg-[var(--success-bg)] text-[var(--success)] border-0"
                          : d.kind === "warning"
                            ? "bg-[var(--error-bg)] text-[var(--error)] border-0"
                            : "bg-[var(--subtle-bg)] text-[var(--text-muted)] border-0"
                      }
                    >
                      {d.kind}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-[var(--text-secondary)] text-xs">
                    {d.category || "—"}
                  </td>
                  <td
                    className={`px-3 py-3 text-right font-mono-num font-semibold ${d.points > 0 ? "text-[var(--success)]" : d.points < 0 ? "text-[var(--error)]" : ""}`}
                  >
                    {d.points > 0 ? `+${d.points}` : d.points}
                  </td>
                  <td className="px-3 py-3 text-xs max-w-md">
                    {d.description}
                  </td>
                </tr>
              ))}
              {(data || []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center text-[var(--text-muted)]"
                  >
                    No incidents recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== ALUMNI
function AlumniTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const { data: alumni } = useQuery({
    queryKey: ["alumni", search],
    queryFn: async () =>
      unwrap(await api.get(`/alumni${search ? `?search=${search}` : ""}`)),
  });
  const { data: stats } = useQuery({
    queryKey: ["alumni-stats"],
    queryFn: async () => unwrap(await api.get("/alumni-stats")),
  });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    batch_year: 2020,
    current_role: "",
    current_company: "",
    city: "",
    email: "",
  });

  const addMut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post("/alumni", {
          ...form,
          batch_year: Number(form.batch_year),
        }),
      ),
    onSuccess: () => {
      toast.success("Alumni added");
      setShow(false);
      qc.invalidateQueries({ queryKey: ["alumni"] });
      qc.invalidateQueries({ queryKey: ["alumni-stats"] });
      setForm({
        full_name: "",
        batch_year: 2020,
        current_role: "",
        current_company: "",
        city: "",
        email: "",
      });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-6 space-y-4">
      <div className="grid md:grid-cols-4 gap-3">
        <StatCard
          label="Total alumni"
          value={stats?.total || 0}
          icon={<GraduationCap className="h-4 w-4" />}
        />
        {(stats?.by_year || []).slice(0, 3).map((y) => (
          <StatCard
            key={y.batch_year}
            label={`Batch ${y.batch_year}`}
            value={y.count}
            icon={<GraduationCap className="h-4 w-4 text-[var(--accent)]" />}
          />
        ))}
      </div>
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row justify-between items-center gap-3">
          <CardTitle className="text-sm font-display flex-shrink-0">
            Alumni directory
          </CardTitle>
          <div className="flex gap-2 flex-1 justify-end">
            <Input
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs h-9"
              data-testid="alumni-search"
            />
            <Dialog open={show} onOpenChange={setShow}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                  data-testid="new-alumni-btn"
                >
                  Add alumni
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add alumni</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Input
                    placeholder="Full name"
                    value={form.full_name}
                    onChange={(e) =>
                      setForm({ ...form, full_name: e.target.value })
                    }
                    data-testid="alumni-name"
                  />
                  <Input
                    type="number"
                    placeholder="Batch year"
                    value={form.batch_year}
                    onChange={(e) =>
                      setForm({ ...form, batch_year: e.target.value })
                    }
                    data-testid="alumni-year"
                  />
                  <Input
                    placeholder="Current role"
                    value={form.current_role}
                    onChange={(e) =>
                      setForm({ ...form, current_role: e.target.value })
                    }
                    data-testid="alumni-role"
                  />
                  <Input
                    placeholder="Current company"
                    value={form.current_company}
                    onChange={(e) =>
                      setForm({ ...form, current_company: e.target.value })
                    }
                    data-testid="alumni-company"
                  />
                  <Input
                    placeholder="City"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    data-testid="alumni-city"
                  />
                  <Input
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    data-testid="alumni-email"
                  />
                </div>
                <DialogFooter>
                  <Button
                    onClick={() => addMut.mutate()}
                    disabled={!form.full_name || addMut.isPending}
                    data-testid="alumni-save"
                  >
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-3">Name</th>
                <th className="text-left px-3 py-3">Batch</th>
                <th className="text-left px-3 py-3">Role</th>
                <th className="text-left px-3 py-3">Company</th>
                <th className="text-left px-3 py-3">City</th>
                <th className="text-left px-3 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {(alumni || []).map((a) => (
                <tr key={a.id} data-testid={`alum-${a.id}`}>
                  <td className="px-3 py-3 font-medium">{a.full_name}</td>
                  <td className="px-3 py-3 font-mono-num">{a.batch_year}</td>
                  <td className="px-3 py-3">{a.current_role || "—"}</td>
                  <td className="px-3 py-3 text-[var(--text-secondary)]">
                    {a.current_company || "—"}
                  </td>
                  <td className="px-3 py-3 text-[var(--text-secondary)]">
                    {a.city || "—"}
                  </td>
                  <td className="px-3 py-3 font-mono-num text-xs">
                    {a.email || "—"}
                  </td>
                </tr>
              ))}
              {(alumni || []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center text-[var(--text-muted)]"
                  >
                    No alumni yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== PLACEMENT
function PlacementTab() {
  const qc = useQueryClient();
  const { data: drives } = useQuery({
    queryKey: ["drives"],
    queryFn: async () => unwrap(await api.get("/placement/drives")),
  });
  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: async () => unwrap(await api.get("/placement/companies")),
  });
  const [showC, setShowC] = useState(false);
  const [showD, setShowD] = useState(false);
  const [cf, setCf] = useState({
    name: "",
    sector: "",
    hq_city: "",
    contact_email: "",
  });
  const [df, setDf] = useState({
    company_id: "",
    role: "",
    ctc_paise: 300000000,
    drive_date: new Date().toISOString().slice(0, 10),
    description: "",
  });

  const cMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/placement/companies", cf)),
    onSuccess: () => {
      toast.success("Company added");
      setShowC(false);
      setCf({ name: "", sector: "", hq_city: "", contact_email: "" });
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const dMut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post("/placement/drives", {
          ...df,
          ctc_paise: Number(df.ctc_paise),
        }),
      ),
    onSuccess: () => {
      toast.success("Drive scheduled");
      setShowD(false);
      qc.invalidateQueries({ queryKey: ["drives"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-4">
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display">
            Companies ({(companies || []).length})
          </CardTitle>
          <Dialog open={showC} onOpenChange={setShowC}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid="new-company-btn">
                Add company
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add company</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input
                  placeholder="Name"
                  value={cf.name}
                  onChange={(e) => setCf({ ...cf, name: e.target.value })}
                  data-testid="company-name"
                />
                <Input
                  placeholder="Sector"
                  value={cf.sector}
                  onChange={(e) => setCf({ ...cf, sector: e.target.value })}
                  data-testid="company-sector"
                />
                <Input
                  placeholder="HQ city"
                  value={cf.hq_city}
                  onChange={(e) => setCf({ ...cf, hq_city: e.target.value })}
                  data-testid="company-city"
                />
                <Input
                  placeholder="Contact email"
                  value={cf.contact_email}
                  onChange={(e) =>
                    setCf({ ...cf, contact_email: e.target.value })
                  }
                  data-testid="company-email"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={() => cMut.mutate()}
                  disabled={!cf.name || cMut.isPending}
                  data-testid="company-save"
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-2">
          {(companies || []).map((c) => (
            <div
              key={c.id}
              className="border-b border-[var(--subtle)] pb-2 last:border-0"
            >
              <div className="font-semibold text-sm">{c.name}</div>
              <div className="text-xs text-[var(--text-muted)]">
                {c.sector || "—"} · {c.hq_city || "—"}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="text-sm font-display">
            Placement drives
          </CardTitle>
          <Dialog open={showD} onOpenChange={setShowD}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="new-drive-btn"
              >
                Schedule drive
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Schedule drive</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Select
                  value={df.company_id}
                  onValueChange={(v) => setDf({ ...df, company_id: v })}
                >
                  <SelectTrigger data-testid="drive-company">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {(companies || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Role"
                  value={df.role}
                  onChange={(e) => setDf({ ...df, role: e.target.value })}
                  data-testid="drive-role"
                />
                <Input
                  type="number"
                  placeholder="CTC (paise/annum)"
                  value={df.ctc_paise}
                  onChange={(e) => setDf({ ...df, ctc_paise: e.target.value })}
                  data-testid="drive-ctc"
                />
                <DatePicker
                  value={df.drive_date}
                  onChange={(date) => setDf({ ...df, drive_date: date })}
                  testId="drive-date"
                />
                <Textarea
                  placeholder="Description"
                  value={df.description}
                  onChange={(e) =>
                    setDf({ ...df, description: e.target.value })
                  }
                  data-testid="drive-desc"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={() => dMut.mutate()}
                  disabled={!df.company_id || !df.role || dMut.isPending}
                  data-testid="drive-save"
                >
                  Schedule
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-2">
          {(drives || []).map((d) => (
            <div
              key={d.id}
              className="border border-[var(--subtle)] rounded p-3"
              data-testid={`drive-${d.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">
                    {d.company?.name} — {d.role}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] font-mono-num">
                    {formatDate(d.drive_date)} · CTC {formatMoney(d.ctc_paise)}/yr
                  </div>
                </div>
                <Badge className="bg-[var(--info-bg)] text-[var(--info)] border-0">
                  {d.applicant_count} applied
                </Badge>
              </div>
            </div>
          ))}
          {(drives || []).length === 0 && (
            <div className="text-sm text-[var(--text-muted)] text-center py-4">
              No drives scheduled.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============== HOSTEL SERVICES
function HostelSvcTab() {
  const qc = useQueryClient();
  const laundryLookup = useLookupValues("hostel_laundry_statuses");
  const maintenanceLookup = useLookupValues("hostel_maintenance_statuses");
  const { data: laundry } = useQuery({
    queryKey: ["laundry"],
    queryFn: async () => unwrap(await api.get("/hostel-laundry")),
  });
  const { data: maint } = useQuery({
    queryKey: ["maint"],
    queryFn: async () => unwrap(await api.get("/hostel-maintenance")),
  });

  const laundryStatuses = (laundryLookup.data?.values || []).map((row) => ({
    value: row.value,
    label: row.label,
  }));
  const maintenanceStatuses = (maintenanceLookup.data?.values || []).map(
    (row) => ({ value: row.value, label: row.label }),
  );
  const laundryStatusOptions = laundryStatuses;
  const maintenanceStatusOptions = maintenanceStatuses;

  const laundryMut = useMutation({
    mutationFn: async ({ id, status }) =>
      unwrap(await api.patch(`/hostel-laundry/${id}/status`, { status })),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["laundry"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const maintMut = useMutation({
    mutationFn: async ({ id, status }) =>
      unwrap(
        await api.patch(`/hostel-maintenance/${id}/status`, {
          status,
          resolution_note: "Fixed",
        }),
      ),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["maint"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-4">
      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="text-sm font-display flex items-center gap-2">
            <Shirt className="h-4 w-4 text-[var(--accent)]" /> Laundry
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(laundry || []).map((l) => (
            <div
              key={l.id}
              className="border border-[var(--subtle)] rounded p-3"
              data-testid={`laundry-${l.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{l.student_name}</div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {l.items_count} items · {l.remark || "—"}
                  </div>
                </div>
                <Select
                  value={l.status}
                  onValueChange={(v) =>
                    laundryMut.mutate({ id: l.id, status: v })
                  }
                >
                  <SelectTrigger
                    className="h-8 w-32 text-xs"
                    data-testid={`laundry-status-${l.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {laundryStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          {(laundry || []).length === 0 && (
            <div className="text-sm text-[var(--text-muted)] text-center py-4">
              No requests.
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="text-sm font-display flex items-center gap-2">
            <Wrench className="h-4 w-4 text-[var(--accent)]" /> Maintenance
            tickets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(maint || []).map((m) => (
            <div
              key={m.id}
              className="border border-[var(--subtle)] rounded p-3"
              data-testid={`maint-${m.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="font-semibold text-sm">
                    Room {m.room?.room_number || m.room?.room_no || "—"} ·{" "}
                    {m.issue_type}
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {m.description}
                  </div>
                </div>
                <Badge
                  className={
                    m.priority === "high"
                      ? "bg-[var(--error-bg)] text-[var(--error)] border-0"
                      : m.priority === "medium"
                        ? "bg-[var(--warning-bg)] text-[var(--warning)] border-0"
                        : "bg-[var(--subtle-bg)] text-[var(--text-muted)] border-0"
                  }
                >
                  {m.priority}
                </Badge>
              </div>
              <div className="flex items-center justify-between mt-2">
                <Badge className="bg-[var(--info-bg)] text-[var(--info)] border-0">
                  {m.status}
                </Badge>
                <Select
                  value={m.status}
                  onValueChange={(v) =>
                    maintMut.mutate({ id: m.id, status: v })
                  }
                >
                  <SelectTrigger
                    className="h-8 w-36 text-xs"
                    data-testid={`maint-status-${m.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {maintenanceStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          {(maint || []).length === 0 && (
            <div className="text-sm text-[var(--text-muted)] text-center py-4">
              No maintenance tickets.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============== helpers
function StatCard({ label, value, icon, accent }) {
  const border =
    accent === "error"
      ? "border-l-4 border-l-[var(--error)]"
      : accent === "success"
        ? "border-l-4 border-l-[var(--success)]"
        : "";
  return (
    <Card className={`border-[var(--subtle)] ${border}`}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider">
            {label}
          </div>
          <div className="text-2xl font-display font-bold mt-1 font-mono-num">
            {value}
          </div>
        </div>
        <div className="opacity-70">{icon}</div>
      </CardContent>
    </Card>
  );
}
